'use client';

export type Language = 'EN' | 'SW' | 'AR' | 'HI' | 'UR' | 'FR' | 'SH' | 'ZH';

export const SUPPORTED_LANGUAGES: { code: Language; label: string; native: string; dir: 'ltr' | 'rtl' }[] = [
  { code: 'EN', label: 'English', native: 'English', dir: 'ltr' },
  { code: 'SW', label: 'Swahili', native: 'Kiswahili', dir: 'ltr' },
  { code: 'SH', label: 'Sheng', native: 'Sheng', dir: 'ltr' },
  { code: 'AR', label: 'Arabic', native: 'العربية', dir: 'rtl' },
  { code: 'HI', label: 'Hindi', native: 'हिन्दी', dir: 'ltr' },
  { code: 'UR', label: 'Urdu', native: 'اردو', dir: 'rtl' },
  { code: 'FR', label: 'French', native: 'Français', dir: 'ltr' },
  { code: 'ZH', label: 'Chinese', native: '中文', dir: 'ltr' },
];

export interface Translations {
  // Navigation
  home: string;
  dashboard: string;
  marketplace: string;
  createOrder: string;
  browseTrips: string;
  myOrders: string;
  activeDeliveries: string;
  liveTracking: string;
  storage: string;
  travelChat: string;
  myFinances: string;
  payments: string;
  notifications: string;
  disputes: string;
  reviews: string;
  helpCenter: string;
  profile: string;
  publishTrip: string;
  myTrips: string;
  myBids: string;
  earnings: string;
  wallet: string;
  getVerified: string;
  settings: string;
  signOut: string;
  // Common
  save: string;
  cancel: string;
  submit: string;
  next: string;
  back: string;
  loading: string;
  saving: string;
  success: string;
  error: string;
  search: string;
  filter: string;
  noResults: string;
  viewAll: string;
  close: string;
  confirm: string;
  delete: string;
  edit: string;
  add: string;
  remove: string;
  // Trip creation
  publishATrip: string;
  routeAndDate: string;
  transport: string;
  capacity: string;
  guarantor: string;
  review: string;
  originCounty: string;
  destinationCounty: string;
  travelDate: string;
  departureTime: string;
  estimatedArrival: string;
  arrivalDate: string;
  arrivalTime: string;
  departureStation: string;
  arrivalStation: string;
  pickupAddress: string;
  dropoffAddress: string;
  stopovers: string;
  addStopover: string;
  stopoverCounty: string;
  stopoverStation: string;
  stopoverArrivalTime: string;
  stopoverDuration: string;
  removeStopover: string;
  stopoverNote: string;
  // Preferences
  preferences: string;
  language: string;
  currency: string;
  notificationSettings: string;
  emailNotifications: string;
  smsNotifications: string;
  pushNotifications: string;
  savePreferences: string;
  preferencesSaved: string;
  // Profile
  myProfile: string;
  fullName: string;
  email: string;
  phone: string;
  changePhoto: string;
  saveProfile: string;
  profileSaved: string;
  // Marketplace
  availableTrips: string;
  placeBid: string;
  bidAmount: string;
  tripDetails: string;
  // Orders
  createNewOrder: string;
  orderDetails: string;
  parcelDetails: string;
  // Sidebar groups
  main: string;
  deliveries: string;
  finance: string;
  account: string;
  // Misc
  verified: string;
  pending: string;
  active: string;
  completed: string;
  cancelled: string;
  open: string;
  bidDetails: string;
  deliveryDetails: string;
  studentKyc: string;
  // Sender Home
  welcomeBack: string;
  activitySnapshot: string;
  activeOrders: string;
  completedOrders: string;
  totalSpent: string;
  avgRating: string;
  inProgress: string;
  deliveredOrders: string;
  paymentsMade: string;
  fromTravelers: string;
  trustScore: string;
  totalDeliveries: string;
  totalOrders: string;
  successRate: string;
  recentOrders: string;
  yourLastOrders: string;
  noOrdersYet: string;
  createFirstOrder: string;
  createOrder: string;
  trackDeliveries: string;
  myReviews: string;
  orderStatusOpen: string;
  orderStatusMatched: string;
  orderStatusInTransit: string;
  orderStatusDelivered: string;
  orderStatusCancelled: string;
  orderStatusClosed: string;
  trustElite: string;
  trustTrusted: string;
  trustVerified: string;
  trustActive: string;
  trustNew: string;
  trustMsgElite: string;
  trustMsgGood: string;
  trustMsgBuild: string;
  bid: string;
  bids: string;
}

const translations: Record<Language, Translations> = {
  EN: {
    home: 'Home',
    dashboard: 'Dashboard',
    marketplace: 'Marketplace',
    createOrder: 'Create Order',
    browseTrips: 'Browse Trips',
    myOrders: 'My Orders',
    activeDeliveries: 'Active Deliveries',
    liveTracking: 'Live Tracking',
    storage: 'Storage',
    travelChat: 'Travel Chat',
    myFinances: 'My Finances',
    payments: 'Payments',
    notifications: 'Notifications',
    disputes: 'Disputes',
    reviews: 'Reviews',
    helpCenter: 'Help Center',
    profile: 'Profile',
    publishTrip: 'Publish Trip',
    myTrips: 'My Trips',
    myBids: 'My Bids',
    earnings: 'Earnings',
    wallet: 'Wallet',
    getVerified: 'Get Verified',
    settings: 'Settings',
    signOut: 'Sign Out',
    save: 'Save',
    cancel: 'Cancel',
    submit: 'Submit',
    next: 'Next',
    back: 'Back',
    loading: 'Loading…',
    saving: 'Saving…',
    success: 'Success',
    error: 'Error',
    search: 'Search',
    filter: 'Filter',
    noResults: 'No results found',
    viewAll: 'View All',
    close: 'Close',
    confirm: 'Confirm',
    delete: 'Delete',
    edit: 'Edit',
    add: 'Add',
    remove: 'Remove',
    publishATrip: 'Publish a Trip',
    routeAndDate: 'Route & Date',
    transport: 'Transport',
    capacity: 'Capacity',
    guarantor: 'Guarantor',
    review: 'Review',
    originCounty: 'Origin County',
    destinationCounty: 'Destination County',
    travelDate: 'Travel Date',
    departureTime: 'Departure Time',
    estimatedArrival: 'Estimated Arrival',
    arrivalDate: 'Arrival Date',
    arrivalTime: 'Arrival Time',
    departureStation: 'Departure Station',
    arrivalStation: 'Arrival Station',
    pickupAddress: 'Pickup Address',
    dropoffAddress: 'Drop-off Address',
    stopovers: 'Stopovers',
    addStopover: 'Add Stopover',
    stopoverCounty: 'Stopover County',
    stopoverStation: 'Stopover Station / Town',
    stopoverArrivalTime: 'Arrival Time at Stop',
    stopoverDuration: 'Duration at Stop (mins)',
    removeStopover: 'Remove Stopover',
    stopoverNote: 'Stopovers help senders along your route find your trip',
    preferences: 'Preferences',
    language: 'Language',
    currency: 'Currency',
    notificationSettings: 'Notification Settings',
    emailNotifications: 'Email Notifications',
    smsNotifications: 'SMS Notifications',
    pushNotifications: 'Push Notifications',
    savePreferences: 'Save Preferences',
    preferencesSaved: 'Preferences saved!',
    myProfile: 'My Profile',
    fullName: 'Full Name',
    email: 'Email',
    phone: 'Phone',
    changePhoto: 'Change Photo',
    saveProfile: 'Save Profile',
    profileSaved: 'Profile saved successfully!',
    availableTrips: 'Available Trips',
    placeBid: 'Place Bid',
    bidAmount: 'Bid Amount',
    tripDetails: 'Trip Details',
    createNewOrder: 'Create New Order',
    orderDetails: 'Order Details',
    parcelDetails: 'Parcel Details',
    main: 'Main',
    deliveries: 'Deliveries',
    finance: 'Finance',
    account: 'Account',
    verified: 'Verified',
    pending: 'Pending',
    active: 'Active',
    completed: 'Completed',
    cancelled: 'Cancelled',
    open: 'Open',
    bidDetails: 'Bid Details',
    deliveryDetails: 'Delivery Details',
    studentKyc: 'Student KYC',
    welcomeBack: 'Welcome back',
    activitySnapshot: "Here's a snapshot of your activity",
    activeOrders: 'Active Orders',
    completedOrders: 'Completed',
    totalSpent: 'Total Spent',
    avgRating: 'Avg Rating',
    inProgress: 'In progress',
    deliveredOrders: 'Delivered orders',
    paymentsMade: 'Payments made',
    fromTravelers: 'From travelers',
    trustScore: 'Trust Score',
    totalDeliveries: 'total deliveries',
    totalOrders: 'Total Orders',
    successRate: 'Success Rate',
    recentOrders: 'Recent Orders',
    yourLastOrders: 'Your last',
    noOrdersYet: 'No orders yet',
    createFirstOrder: 'Create your first order to get started',
    createOrder: 'Create Order',
    trackDeliveries: 'Track Deliveries',
    myReviews: 'My Reviews',
    orderStatusOpen: 'Open',
    orderStatusMatched: 'Matched',
    orderStatusInTransit: 'In Transit',
    orderStatusDelivered: 'Delivered',
    orderStatusCancelled: 'Cancelled',
    orderStatusClosed: 'Closed',
    trustElite: 'Elite Customer',
    trustTrusted: 'Trusted Customer',
    trustVerified: 'Verified Customer',
    trustActive: 'Active Customer',
    trustNew: 'New Customer',
    trustMsgElite: "You're a highly trusted sender on MuyuRide.",
    trustMsgGood: 'Complete more orders to reach Elite status.',
    trustMsgBuild: 'Build your reputation with successful deliveries.',
    bid: 'bid',
    bids: 'bids',
  },
  SW: {
    home: 'Nyumbani',
    dashboard: 'Dashibodi',
    marketplace: 'Soko',
    createOrder: 'Unda Agizo',
    browseTrips: 'Tafuta Safari',
    myOrders: 'Maagizo Yangu',
    activeDeliveries: 'Usafirishaji Unaoendelea',
    liveTracking: 'Ufuatiliaji wa Moja kwa Moja',
    storage: 'Hifadhi',
    travelChat: 'Mazungumzo ya Safari',
    myFinances: 'Fedha Zangu',
    payments: 'Malipo',
    notifications: 'Arifa',
    disputes: 'Migogoro',
    reviews: 'Maoni',
    helpCenter: 'Kituo cha Msaada',
    profile: 'Wasifu',
    publishTrip: 'Chapisha Safari',
    myTrips: 'Safari Zangu',
    myBids: 'Zabuni Zangu',
    earnings: 'Mapato',
    wallet: 'Mkoba',
    getVerified: 'Thibitisha',
    settings: 'Mipangilio',
    signOut: 'Toka',
    save: 'Hifadhi',
    cancel: 'Ghairi',
    submit: 'Wasilisha',
    next: 'Ifuatayo',
    back: 'Rudi',
    loading: 'Inapakia…',
    saving: 'Inahifadhi…',
    success: 'Imefanikiwa',
    error: 'Hitilafu',
    search: 'Tafuta',
    filter: 'Chuja',
    noResults: 'Hakuna matokeo',
    viewAll: 'Tazama Yote',
    close: 'Funga',
    confirm: 'Thibitisha',
    delete: 'Futa',
    edit: 'Hariri',
    add: 'Ongeza',
    remove: 'Ondoa',
    publishATrip: 'Chapisha Safari',
    routeAndDate: 'Njia na Tarehe',
    transport: 'Usafiri',
    capacity: 'Uwezo',
    guarantor: 'Mdhamini',
    review: 'Kagua',
    originCounty: 'Kaunti ya Asili',
    destinationCounty: 'Kaunti ya Lengwa',
    travelDate: 'Tarehe ya Safari',
    departureTime: 'Wakati wa Kuondoka',
    estimatedArrival: 'Wakati wa Kuwasili (Tahmini)',
    arrivalDate: 'Tarehe ya Kuwasili',
    arrivalTime: 'Wakati wa Kuwasili',
    departureStation: 'Kituo cha Kuondokea',
    arrivalStation: 'Kituo cha Kuwasilia',
    pickupAddress: 'Anwani ya Kuchukua',
    dropoffAddress: 'Anwani ya Kuacha',
    stopovers: 'Vituo vya Kupumzika',
    addStopover: 'Ongeza Kituo',
    stopoverCounty: 'Kaunti ya Kituo',
    stopoverStation: 'Kituo / Mji wa Kusimama',
    stopoverArrivalTime: 'Wakati wa Kuwasili Kituoni',
    stopoverDuration: 'Muda Kituoni (dakika)',
    removeStopover: 'Ondoa Kituo',
    stopoverNote: 'Vituo husaidia watumaji njiani kupata safari yako',
    preferences: 'Mapendeleo',
    language: 'Lugha',
    currency: 'Sarafu',
    notificationSettings: 'Mipangilio ya Arifa',
    emailNotifications: 'Arifa za Barua Pepe',
    smsNotifications: 'Arifa za SMS',
    pushNotifications: 'Arifa za Kusukuma',
    savePreferences: 'Hifadhi Mapendeleo',
    preferencesSaved: 'Mapendeleo yamehifadhiwa!',
    myProfile: 'Wasifu Wangu',
    fullName: 'Jina Kamili',
    email: 'Barua Pepe',
    phone: 'Simu',
    changePhoto: 'Badilisha Picha',
    saveProfile: 'Hifadhi Wasifu',
    profileSaved: 'Wasifu umehifadhiwa!',
    availableTrips: 'Safari Zinazopatikana',
    placeBid: 'Toa Zabuni',
    bidAmount: 'Kiasi cha Zabuni',
    tripDetails: 'Maelezo ya Safari',
    createNewOrder: 'Unda Agizo Jipya',
    orderDetails: 'Maelezo ya Agizo',
    parcelDetails: 'Maelezo ya Kifurushi',
    main: 'Kuu',
    deliveries: 'Usafirishaji',
    finance: 'Fedha',
    account: 'Akaunti',
    verified: 'Imethibitishwa',
    pending: 'Inasubiri',
    active: 'Inafanya Kazi',
    completed: 'Imekamilika',
    cancelled: 'Imeghairiwa',
    open: 'Wazi',
    bidDetails: 'Maelezo ya Zabuni',
    deliveryDetails: 'Maelezo ya Usafirishaji',
    studentKyc: 'KYC ya Mwanafunzi',
    welcomeBack: 'Karibu tena',
    activitySnapshot: 'Hapa kuna muhtasari wa shughuli zako',
    activeOrders: 'Maagizo Yanayoendelea',
    completedOrders: 'Yaliyokamilika',
    totalSpent: 'Jumla Iliyotumika',
    avgRating: 'Wastani wa Ukadiriaji',
    inProgress: 'Inaendelea',
    deliveredOrders: 'Maagizo yaliyowasilishwa',
    paymentsMade: 'Malipo yaliyofanywa',
    fromTravelers: 'Kutoka kwa wasafiri',
    trustScore: 'Alama ya Uaminifu',
    totalDeliveries: 'jumla ya usafirishaji',
    totalOrders: 'Jumla ya Maagizo',
    successRate: 'Kiwango cha Mafanikio',
    recentOrders: 'Maagizo ya Hivi Karibuni',
    yourLastOrders: 'Maagizo yako ya mwisho',
    noOrdersYet: 'Hakuna maagizo bado',
    createFirstOrder: 'Unda agizo lako la kwanza kuanza',
    createOrder: 'Unda Agizo',
    trackDeliveries: 'Fuatilia Usafirishaji',
    myReviews: 'Maoni Yangu',
    orderStatusOpen: 'Wazi',
    orderStatusMatched: 'Imeoanishwa',
    orderStatusInTransit: 'Inasafirishwa',
    orderStatusDelivered: 'Imewasilishwa',
    orderStatusCancelled: 'Imeghairiwa',
    orderStatusClosed: 'Imefungwa',
    trustElite: 'Mteja Bora',
    trustTrusted: 'Mteja Anayeaminiwa',
    trustVerified: 'Mteja Aliyethibitishwa',
    trustActive: 'Mteja Anayefanya Kazi',
    trustNew: 'Mteja Mpya',
    trustMsgElite: 'Wewe ni mtumaji anayeaminiwa sana kwenye MuyuRide.',
    trustMsgGood: 'Kamilisha maagizo zaidi kufikia hali ya Bora.',
    trustMsgBuild: 'Jenga sifa yako kwa usafirishaji uliofanikiwa.',
    bid: 'zabuni',
    bids: 'zabuni',
  },
  SH: {
    home: 'Home',
    dashboard: 'Dashboard',
    marketplace: 'Soko',
    createOrder: 'Fanya Order',
    browseTrips: 'Angalia Trips',
    myOrders: 'Orders Zangu',
    activeDeliveries: 'Deliveries Zinaenda',
    liveTracking: 'Track Live',
    storage: 'Storage',
    travelChat: 'Travel Chat',
    myFinances: 'Pesa Zangu',
    payments: 'Payments',
    notifications: 'Notifications',
    disputes: 'Disputes',
    reviews: 'Reviews',
    helpCenter: 'Help Center',
    profile: 'Profile',
    publishTrip: 'Post Trip',
    myTrips: 'Trips Zangu',
    myBids: 'Bids Zangu',
    earnings: 'Mapato',
    wallet: 'Wallet',
    getVerified: 'Jiverify',
    settings: 'Settings',
    signOut: 'Sign Out',
    save: 'Save',
    cancel: 'Cancel',
    submit: 'Submit',
    next: 'Next',
    back: 'Back',
    loading: 'Inapakia…',
    saving: 'Inasave…',
    success: 'Imefanya',
    error: 'Kuna Error',
    search: 'Tafuta',
    filter: 'Filter',
    noResults: 'Hakuna kitu',
    viewAll: 'Ona Yote',
    close: 'Funga',
    confirm: 'Confirm',
    delete: 'Delete',
    edit: 'Edit',
    add: 'Ongeza',
    remove: 'Remove',
    publishATrip: 'Post Trip',
    routeAndDate: 'Route na Date',
    transport: 'Transport',
    capacity: 'Capacity',
    guarantor: 'Guarantor',
    review: 'Review',
    originCounty: 'Unatoka Wapi',
    destinationCounty: 'Unaenda Wapi',
    travelDate: 'Tarehe ya Safari',
    departureTime: 'Unaondoka Saa Ngapi',
    estimatedArrival: 'Unafika Saa Ngapi',
    arrivalDate: 'Tarehe ya Kufika',
    arrivalTime: 'Wakati wa Kufika',
    departureStation: 'Stage ya Kuondoka',
    arrivalStation: 'Stage ya Kufika',
    pickupAddress: 'Pickup Address',
    dropoffAddress: 'Drop-off Address',
    stopovers: 'Stopovers',
    addStopover: 'Ongeza Stopover',
    stopoverCounty: 'County ya Stopover',
    stopoverStation: 'Stage / Mji wa Kusimama',
    stopoverArrivalTime: 'Unafika Saa Ngapi Hapa',
    stopoverDuration: 'Unakaa Muda Gani (mins)',
    removeStopover: 'Remove Stopover',
    stopoverNote: 'Stopovers husaidia wasender wapate trip yako',
    preferences: 'Preferences',
    language: 'Lugha',
    currency: 'Currency',
    notificationSettings: 'Notification Settings',
    emailNotifications: 'Email Notifications',
    smsNotifications: 'SMS Notifications',
    pushNotifications: 'Push Notifications',
    savePreferences: 'Save Preferences',
    preferencesSaved: 'Preferences zimesavewa!',
    myProfile: 'Profile Yangu',
    fullName: 'Jina Lako Lote',
    email: 'Email',
    phone: 'Simu',
    changePhoto: 'Badilisha Picha',
    saveProfile: 'Save Profile',
    profileSaved: 'Profile imesavewa!',
    availableTrips: 'Trips Zinapatikana',
    placeBid: 'Weka Bid',
    bidAmount: 'Bid Amount',
    tripDetails: 'Trip Details',
    createNewOrder: 'Fanya Order Mpya',
    orderDetails: 'Order Details',
    parcelDetails: 'Parcel Details',
    main: 'Main',
    deliveries: 'Deliveries',
    finance: 'Pesa',
    account: 'Account',
    verified: 'Verified',
    pending: 'Inasubiri',
    active: 'Inafanya Kazi',
    completed: 'Imekwisha',
    cancelled: 'Imeghairiwa',
    open: 'Wazi',
    bidDetails: 'Bid Details',
    deliveryDetails: 'Delivery Details',
    studentKyc: 'Student KYC',
    welcomeBack: 'Sasa wewe ni back',
    activitySnapshot: 'Hii ndiyo hali yako sasa',
    activeOrders: 'Orders Zinaenda',
    completedOrders: 'Zilizokwisha',
    totalSpent: 'Pesa Uliyotumia',
    avgRating: 'Rating Yako',
    inProgress: 'Inaendelea',
    deliveredOrders: 'Orders zilizofika',
    paymentsMade: 'Malipo yaliyofanywa',
    fromTravelers: 'Kutoka kwa wasafiri',
    trustScore: 'Trust Score',
    totalDeliveries: 'deliveries zote',
    totalOrders: 'Orders Zote',
    successRate: 'Success Rate',
    recentOrders: 'Orders za Hivi Karibuni',
    yourLastOrders: 'Orders zako za mwisho',
    noOrdersYet: 'Bado hujafanya order',
    createFirstOrder: 'Fanya order yako ya kwanza uanze',
    createOrder: 'Fanya Order',
    trackDeliveries: 'Track Deliveries',
    myReviews: 'Reviews Zangu',
    orderStatusOpen: 'Wazi',
    orderStatusMatched: 'Imeoanishwa',
    orderStatusInTransit: 'Inasafirishwa',
    orderStatusDelivered: 'Imefika',
    orderStatusCancelled: 'Imeghairiwa',
    orderStatusClosed: 'Imefungwa',
    trustElite: 'Customer wa Juu',
    trustTrusted: 'Customer Anayeaminiwa',
    trustVerified: 'Customer Aliyethibitishwa',
    trustActive: 'Customer Anayefanya Kazi',
    trustNew: 'Customer Mpya',
    trustMsgElite: 'Wewe ni msender wa kuaminiwa sana kwa MuyuRide.',
    trustMsgGood: 'Kamilisha orders zaidi ufike Elite.',
    trustMsgBuild: 'Jenga rep yako na deliveries zilizofanikiwa.',
    bid: 'bid',
    bids: 'bids',
  },
  AR: {
    home: 'الرئيسية',
    dashboard: 'لوحة التحكم',
    marketplace: 'السوق',
    createOrder: 'إنشاء طلب',
    browseTrips: 'تصفح الرحلات',
    myOrders: 'طلباتي',
    activeDeliveries: 'التوصيلات النشطة',
    liveTracking: 'التتبع المباشر',
    storage: 'التخزين',
    travelChat: 'محادثة السفر',
    myFinances: 'ماليتي',
    payments: 'المدفوعات',
    notifications: 'الإشعارات',
    disputes: 'النزاعات',
    reviews: 'التقييمات',
    helpCenter: 'مركز المساعدة',
    profile: 'الملف الشخصي',
    publishTrip: 'نشر رحلة',
    myTrips: 'رحلاتي',
    myBids: 'عروضي',
    earnings: 'الأرباح',
    wallet: 'المحفظة',
    getVerified: 'التحقق',
    settings: 'الإعدادات',
    signOut: 'تسجيل الخروج',
    save: 'حفظ',
    cancel: 'إلغاء',
    submit: 'إرسال',
    next: 'التالي',
    back: 'رجوع',
    loading: 'جارٍ التحميل…',
    saving: 'جارٍ الحفظ…',
    success: 'نجاح',
    error: 'خطأ',
    search: 'بحث',
    filter: 'تصفية',
    noResults: 'لا توجد نتائج',
    viewAll: 'عرض الكل',
    close: 'إغلاق',
    confirm: 'تأكيد',
    delete: 'حذف',
    edit: 'تعديل',
    add: 'إضافة',
    remove: 'إزالة',
    publishATrip: 'نشر رحلة',
    routeAndDate: 'المسار والتاريخ',
    transport: 'وسيلة النقل',
    capacity: 'السعة',
    guarantor: 'الضامن',
    review: 'مراجعة',
    originCounty: 'مقاطعة الأصل',
    destinationCounty: 'مقاطعة الوجهة',
    travelDate: 'تاريخ السفر',
    departureTime: 'وقت المغادرة',
    estimatedArrival: 'الوصول المتوقع',
    arrivalDate: 'تاريخ الوصول',
    arrivalTime: 'وقت الوصول',
    departureStation: 'محطة المغادرة',
    arrivalStation: 'محطة الوصول',
    pickupAddress: 'عنوان الاستلام',
    dropoffAddress: 'عنوان التسليم',
    stopovers: 'محطات التوقف',
    addStopover: 'إضافة محطة توقف',
    stopoverCounty: 'مقاطعة التوقف',
    stopoverStation: 'محطة / مدينة التوقف',
    stopoverArrivalTime: 'وقت الوصول للمحطة',
    stopoverDuration: 'مدة التوقف (دقائق)',
    removeStopover: 'إزالة محطة التوقف',
    stopoverNote: 'تساعد محطات التوقف المرسلين على طول طريقك في العثور على رحلتك',
    preferences: 'التفضيلات',
    language: 'اللغة',
    currency: 'العملة',
    notificationSettings: 'إعدادات الإشعارات',
    emailNotifications: 'إشعارات البريد الإلكتروني',
    smsNotifications: 'إشعارات الرسائل القصيرة',
    pushNotifications: 'الإشعارات الفورية',
    savePreferences: 'حفظ التفضيلات',
    preferencesSaved: 'تم حفظ التفضيلات!',
    myProfile: 'ملفي الشخصي',
    fullName: 'الاسم الكامل',
    email: 'البريد الإلكتروني',
    phone: 'الهاتف',
    changePhoto: 'تغيير الصورة',
    saveProfile: 'حفظ الملف الشخصي',
    profileSaved: 'تم حفظ الملف الشخصي!',
    availableTrips: 'الرحلات المتاحة',
    placeBid: 'تقديم عرض',
    bidAmount: 'مبلغ العرض',
    tripDetails: 'تفاصيل الرحلة',
    createNewOrder: 'إنشاء طلب جديد',
    orderDetails: 'تفاصيل الطلب',
    parcelDetails: 'تفاصيل الطرد',
    main: 'الرئيسي',
    deliveries: 'التوصيلات',
    finance: 'المالية',
    account: 'الحساب',
    verified: 'موثق',
    pending: 'قيد الانتظار',
    active: 'نشط',
    completed: 'مكتمل',
    cancelled: 'ملغى',
    open: 'مفتوح',
    bidDetails: 'تفاصيل العرض',
    deliveryDetails: 'تفاصيل التوصيل',
    studentKyc: 'KYC الطالب',
    welcomeBack: 'مرحباً بعودتك',
    activitySnapshot: 'إليك لمحة عن نشاطك',
    activeOrders: 'الطلبات النشطة',
    completedOrders: 'المكتملة',
    totalSpent: 'إجمالي الإنفاق',
    avgRating: 'متوسط التقييم',
    inProgress: 'قيد التنفيذ',
    deliveredOrders: 'الطلبات المُسلَّمة',
    paymentsMade: 'المدفوعات المنجزة',
    fromTravelers: 'من المسافرين',
    trustScore: 'درجة الثقة',
    totalDeliveries: 'إجمالي التوصيلات',
    totalOrders: 'إجمالي الطلبات',
    successRate: 'معدل النجاح',
    recentOrders: 'الطلبات الأخيرة',
    yourLastOrders: 'آخر طلباتك',
    noOrdersYet: 'لا توجد طلبات بعد',
    createFirstOrder: 'أنشئ طلبك الأول للبدء',
    createOrder: 'إنشاء طلب',
    trackDeliveries: 'تتبع التوصيلات',
    myReviews: 'تقييماتي',
    orderStatusOpen: 'مفتوح',
    orderStatusMatched: 'مطابق',
    orderStatusInTransit: 'في الطريق',
    orderStatusDelivered: 'تم التسليم',
    orderStatusCancelled: 'ملغى',
    orderStatusClosed: 'مغلق',
    trustElite: 'عميل نخبة',
    trustTrusted: 'عميل موثوق',
    trustVerified: 'عميل محقق',
    trustActive: 'عميل نشط',
    trustNew: 'عميل جديد',
    trustMsgElite: 'أنت مرسل موثوق للغاية على MuyuRide.',
    trustMsgGood: 'أكمل المزيد من الطلبات للوصول إلى مستوى النخبة.',
    trustMsgBuild: 'ابنِ سمعتك من خلال التوصيلات الناجحة.',
    bid: 'عرض',
    bids: 'عروض',
  },
  HI: {
    home: 'होम',
    dashboard: 'डैशबोर्ड',
    marketplace: 'बाज़ार',
    createOrder: 'ऑर्डर बनाएं',
    browseTrips: 'यात्राएं देखें',
    myOrders: 'मेरे ऑर्डर',
    activeDeliveries: 'सक्रिय डिलीवरी',
    liveTracking: 'लाइव ट्रैकिंग',
    storage: 'भंडारण',
    travelChat: 'यात्रा चैट',
    myFinances: 'मेरी वित्त',
    payments: 'भुगतान',
    notifications: 'सूचनाएं',
    disputes: 'विवाद',
    reviews: 'समीक्षाएं',
    helpCenter: 'सहायता केंद्र',
    profile: 'प्रोफ़ाइल',
    publishTrip: 'यात्रा प्रकाशित करें',
    myTrips: 'मेरी यात्राएं',
    myBids: 'मेरी बोलियां',
    earnings: 'कमाई',
    wallet: 'वॉलेट',
    getVerified: 'सत्यापित हों',
    settings: 'सेटिंग्स',
    signOut: 'साइन आउट',
    save: 'सहेजें',
    cancel: 'रद्द करें',
    submit: 'जमा करें',
    next: 'अगला',
    back: 'वापस',
    loading: 'लोड हो रहा है…',
    saving: 'सहेजा जा रहा है…',
    success: 'सफलता',
    error: 'त्रुटि',
    search: 'खोजें',
    filter: 'फ़िल्टर',
    noResults: 'कोई परिणाम नहीं',
    viewAll: 'सभी देखें',
    close: 'बंद करें',
    confirm: 'पुष्टि करें',
    delete: 'हटाएं',
    edit: 'संपादित करें',
    add: 'जोड़ें',
    remove: 'हटाएं',
    publishATrip: 'यात्रा प्रकाशित करें',
    routeAndDate: 'मार्ग और तारीख',
    transport: 'परिवहन',
    capacity: 'क्षमता',
    guarantor: 'गारंटर',
    review: 'समीक्षा',
    originCounty: 'मूल काउंटी',
    destinationCounty: 'गंतव्य काउंटी',
    travelDate: 'यात्रा तिथि',
    departureTime: 'प्रस्थान समय',
    estimatedArrival: 'अनुमानित आगमन',
    arrivalDate: 'आगमन तिथि',
    arrivalTime: 'आगमन समय',
    departureStation: 'प्रस्थान स्टेशन',
    arrivalStation: 'आगमन स्टेशन',
    pickupAddress: 'पिकअप पता',
    dropoffAddress: 'ड्रॉप-ऑफ पता',
    stopovers: 'पड़ाव',
    addStopover: 'पड़ाव जोड़ें',
    stopoverCounty: 'पड़ाव काउंटी',
    stopoverStation: 'पड़ाव स्टेशन / शहर',
    stopoverArrivalTime: 'पड़ाव पर आगमन समय',
    stopoverDuration: 'पड़ाव पर समय (मिनट)',
    removeStopover: 'पड़ाव हटाएं',
    stopoverNote: 'पड़ाव आपके मार्ग पर प्रेषकों को आपकी यात्रा खोजने में मदद करते हैं',
    preferences: 'प्राथमिकताएं',
    language: 'भाषा',
    currency: 'मुद्रा',
    notificationSettings: 'सूचना सेटिंग्स',
    emailNotifications: 'ईमेल सूचनाएं',
    smsNotifications: 'SMS सूचनाएं',
    pushNotifications: 'पुश सूचनाएं',
    savePreferences: 'प्राथमिकताएं सहेजें',
    preferencesSaved: 'प्राथमिकताएं सहेजी गईं!',
    myProfile: 'मेरी प्रोफ़ाइल',
    fullName: 'पूरा नाम',
    email: 'ईमेल',
    phone: 'फ़ोन',
    changePhoto: 'फ़ोटो बदलें',
    saveProfile: 'प्रोफ़ाइल सहेजें',
    profileSaved: 'प्रोफ़ाइल सफलतापूर्वक सहेजी गई!',
    availableTrips: 'उपलब्ध यात्राएं',
    placeBid: 'बोली लगाएं',
    bidAmount: 'बोली राशि',
    tripDetails: 'यात्रा विवरण',
    createNewOrder: 'नया ऑर्डर बनाएं',
    orderDetails: 'ऑर्डर विवरण',
    parcelDetails: 'पार्सल विवरण',
    main: 'मुख्य',
    deliveries: 'डिलीवरी',
    finance: 'वित्त',
    account: 'खाता',
    verified: 'सत्यापित',
    pending: 'लंबित',
    active: 'सक्रिय',
    completed: 'पूर्ण',
    cancelled: 'रद्द',
    open: 'खुला',
    bidDetails: 'बोली विवरण',
    deliveryDetails: 'डिलीवरी विवरण',
    studentKyc: 'छात्र KYC',
    welcomeBack: 'वापस स्वागत है',
    activitySnapshot: 'यहाँ आपकी गतिविधि का सारांश है',
    activeOrders: 'सक्रिय ऑर्डर',
    completedOrders: 'पूर्ण',
    totalSpent: 'कुल खर्च',
    avgRating: 'औसत रेटिंग',
    inProgress: 'प्रगति में',
    deliveredOrders: 'डिलीवर किए गए ऑर्डर',
    paymentsMade: 'किए गए भुगतान',
    fromTravelers: 'यात्रियों से',
    trustScore: 'विश्वास स्कोर',
    totalDeliveries: 'कुल डिलीवरी',
    totalOrders: 'कुल ऑर्डर',
    successRate: 'सफलता दर',
    recentOrders: 'हाल के ऑर्डर',
    yourLastOrders: 'आपके अंतिम',
    noOrdersYet: 'अभी तक कोई ऑर्डर नहीं',
    createFirstOrder: 'शुरू करने के लिए अपना पहला ऑर्डर बनाएं',
    createOrder: 'ऑर्डर बनाएं',
    trackDeliveries: 'डिलीवरी ट्रैक करें',
    myReviews: 'मेरी समीक्षाएं',
    orderStatusOpen: 'खुला',
    orderStatusMatched: 'मिलान किया',
    orderStatusInTransit: 'रास्ते में',
    orderStatusDelivered: 'डिलीवर',
    orderStatusCancelled: 'रद्द',
    orderStatusClosed: 'बंद',
    trustElite: 'एलीट ग्राहक',
    trustTrusted: 'विश्वसनीय ग्राहक',
    trustVerified: 'सत्यापित ग्राहक',
    trustActive: 'सक्रिय ग्राहक',
    trustNew: 'नया ग्राहक',
    trustMsgElite: 'आप MuyuRide पर एक अत्यधिक विश्वसनीय प्रेषक हैं।',
    trustMsgGood: 'एलीट स्तर तक पहुंचने के लिए अधिक ऑर्डर पूरे करें।',
    trustMsgBuild: 'सफल डिलीवरी के साथ अपनी प्रतिष्ठा बनाएं।',
    bid: 'बोली',
    bids: 'बोलियां',
  },
  UR: {
    home: 'ہوم',
    dashboard: 'ڈیش بورڈ',
    marketplace: 'بازار',
    createOrder: 'آرڈر بنائیں',
    browseTrips: 'سفر دیکھیں',
    myOrders: 'میرے آرڈر',
    activeDeliveries: 'فعال ترسیلات',
    liveTracking: 'لائیو ٹریکنگ',
    storage: 'ذخیرہ',
    travelChat: 'سفر چیٹ',
    myFinances: 'میری مالیات',
    payments: 'ادائیگیاں',
    notifications: 'اطلاعات',
    disputes: 'تنازعات',
    reviews: 'جائزے',
    helpCenter: 'مدد مرکز',
    profile: 'پروفائل',
    publishTrip: 'سفر شائع کریں',
    myTrips: 'میرے سفر',
    myBids: 'میری بولیاں',
    earnings: 'آمدنی',
    wallet: 'بٹوہ',
    getVerified: 'تصدیق کروائیں',
    settings: 'ترتیبات',
    signOut: 'سائن آؤٹ',
    save: 'محفوظ کریں',
    cancel: 'منسوخ',
    submit: 'جمع کریں',
    next: 'اگلا',
    back: 'واپس',
    loading: 'لوڈ ہو رہا ہے…',
    saving: 'محفوظ ہو رہا ہے…',
    success: 'کامیابی',
    error: 'خطا',
    search: 'تلاش',
    filter: 'فلٹر',
    noResults: 'کوئی نتیجہ نہیں',
    viewAll: 'سب دیکھیں',
    close: 'بند کریں',
    confirm: 'تصدیق کریں',
    delete: 'حذف کریں',
    edit: 'ترمیم',
    add: 'شامل کریں',
    remove: 'ہٹائیں',
    publishATrip: 'سفر شائع کریں',
    routeAndDate: 'راستہ اور تاریخ',
    transport: 'نقل و حمل',
    capacity: 'گنجائش',
    guarantor: 'ضامن',
    review: 'جائزہ',
    originCounty: 'اصل کاؤنٹی',
    destinationCounty: 'منزل کاؤنٹی',
    travelDate: 'سفر کی تاریخ',
    departureTime: 'روانگی کا وقت',
    estimatedArrival: 'متوقع آمد',
    arrivalDate: 'آمد کی تاریخ',
    arrivalTime: 'آمد کا وقت',
    departureStation: 'روانگی اسٹیشن',
    arrivalStation: 'آمد اسٹیشن',
    pickupAddress: 'پک اپ پتہ',
    dropoffAddress: 'ڈراپ آف پتہ',
    stopovers: 'درمیانی پڑاؤ',
    addStopover: 'پڑاؤ شامل کریں',
    stopoverCounty: 'پڑاؤ کاؤنٹی',
    stopoverStation: 'پڑاؤ اسٹیشن / شہر',
    stopoverArrivalTime: 'پڑاؤ پر آمد کا وقت',
    stopoverDuration: 'پڑاؤ پر وقت (منٹ)',
    removeStopover: 'پڑاؤ ہٹائیں',
    stopoverNote: 'پڑاؤ آپ کے راستے پر بھیجنے والوں کو آپ کا سفر تلاش کرنے میں مدد کرتے ہیں',
    preferences: 'ترجیحات',
    language: 'زبان',
    currency: 'کرنسی',
    notificationSettings: 'اطلاع کی ترتیبات',
    emailNotifications: 'ای میل اطلاعات',
    smsNotifications: 'SMS اطلاعات',
    pushNotifications: 'پش اطلاعات',
    savePreferences: 'ترجیحات محفوظ کریں',
    preferencesSaved: 'ترجیحات محفوظ ہو گئیں!',
    myProfile: 'میری پروفائل',
    fullName: 'پورا نام',
    email: 'ای میل',
    phone: 'فون',
    changePhoto: 'تصویر تبدیل کریں',
    saveProfile: 'پروفائل محفوظ کریں',
    profileSaved: 'پروفائل کامیابی سے محفوظ ہو گئی!',
    availableTrips: 'دستیاب سفر',
    placeBid: 'بولی لگائیں',
    bidAmount: 'بولی کی رقم',
    tripDetails: 'سفر کی تفصیلات',
    createNewOrder: 'نیا آرڈر بنائیں',
    orderDetails: 'آرڈر کی تفصیلات',
    parcelDetails: 'پارسل کی تفصیلات',
    main: 'مرکزی',
    deliveries: 'ترسیلات',
    finance: 'مالیات',
    account: 'اکاؤنٹ',
    verified: 'تصدیق شدہ',
    pending: 'زیر التواء',
    active: 'فعال',
    completed: 'مکمل',
    cancelled: 'منسوخ',
    open: 'کھلا',
    bidDetails: 'بولی کی تفصیلات',
    deliveryDetails: 'ترسیل کی تفصیلات',
    studentKyc: 'طالب علم KYC',
    welcomeBack: 'واپس خوش آمدید',
    activitySnapshot: 'یہاں آپ کی سرگرمی کا خلاصہ ہے',
    activeOrders: 'فعال آرڈر',
    completedOrders: 'مکمل',
    totalSpent: 'کل خرچ',
    avgRating: 'اوسط درجہ بندی',
    inProgress: 'جاری',
    deliveredOrders: 'ترسیل شدہ آرڈر',
    paymentsMade: 'کی گئی ادائیگیاں',
    fromTravelers: 'مسافروں سے',
    trustScore: 'اعتماد اسکور',
    totalDeliveries: 'کل ترسیلات',
    totalOrders: 'کل آرڈر',
    successRate: 'کامیابی کی شرح',
    recentOrders: 'حالیہ آرڈر',
    yourLastOrders: 'آپ کے آخری',
    noOrdersYet: 'ابھی تک کوئی آرڈر نہیں',
    createFirstOrder: 'شروع کرنے کے لیے اپنا پہلا آرڈر بنائیں',
    createOrder: 'آرڈر بنائیں',
    trackDeliveries: 'ترسیلات ٹریک کریں',
    myReviews: 'میرے جائزے',
    orderStatusOpen: 'کھلا',
    orderStatusMatched: 'مطابق',
    orderStatusInTransit: 'راستے میں',
    orderStatusDelivered: 'ترسیل شدہ',
    orderStatusCancelled: 'منسوخ',
    orderStatusClosed: 'بند',
    trustElite: 'ایلیٹ گاہک',
    trustTrusted: 'قابل اعتماد گاہک',
    trustVerified: 'تصدیق شدہ گاہک',
    trustActive: 'فعال گاہک',
    trustNew: 'نیا گاہک',
    trustMsgElite: 'آپ MuyuRide پر ایک انتہائی قابل اعتماد بھیجنے والے ہیں۔',
    trustMsgGood: 'ایلیٹ درجے تک پہنچنے کے لیے مزید آرڈر مکمل کریں۔',
    trustMsgBuild: 'کامیاب ترسیلات کے ساتھ اپنی ساکھ بنائیں۔',
    bid: 'بولی',
    bids: 'بولیاں',
  },
  FR: {
    home: 'Accueil',
    dashboard: 'Tableau de bord',
    marketplace: 'Marché',
    createOrder: 'Créer une commande',
    browseTrips: 'Parcourir les voyages',
    myOrders: 'Mes commandes',
    activeDeliveries: 'Livraisons actives',
    liveTracking: 'Suivi en direct',
    storage: 'Stockage',
    travelChat: 'Chat de voyage',
    myFinances: 'Mes finances',
    payments: 'Paiements',
    notifications: 'Notifications',
    disputes: 'Litiges',
    reviews: 'Avis',
    helpCenter: "Centre d'aide",
    profile: 'Profil',
    publishTrip: 'Publier un voyage',
    myTrips: 'Mes voyages',
    myBids: 'Mes offres',
    earnings: 'Revenus',
    wallet: 'Portefeuille',
    getVerified: 'Se vérifier',
    settings: 'Paramètres',
    signOut: 'Se déconnecter',
    save: 'Enregistrer',
    cancel: 'Annuler',
    submit: 'Soumettre',
    next: 'Suivant',
    back: 'Retour',
    loading: 'Chargement…',
    saving: 'Enregistrement…',
    success: 'Succès',
    error: 'Erreur',
    search: 'Rechercher',
    filter: 'Filtrer',
    noResults: 'Aucun résultat',
    viewAll: 'Voir tout',
    close: 'Fermer',
    confirm: 'Confirmer',
    delete: 'Supprimer',
    edit: 'Modifier',
    add: 'Ajouter',
    remove: 'Supprimer',
    publishATrip: 'Publier un voyage',
    routeAndDate: 'Itinéraire et date',
    transport: 'Transport',
    capacity: 'Capacité',
    guarantor: 'Garant',
    review: 'Révision',
    originCounty: "Comté d'origine",
    destinationCounty: 'Comté de destination',
    travelDate: 'Date de voyage',
    departureTime: 'Heure de départ',
    estimatedArrival: "Arrivée estimée",
    arrivalDate: "Date d'arrivée",
    arrivalTime: "Heure d'arrivée",
    departureStation: 'Gare de départ',
    arrivalStation: "Gare d'arrivée",
    pickupAddress: 'Adresse de ramassage',
    dropoffAddress: 'Adresse de dépôt',
    stopovers: 'Escales',
    addStopover: 'Ajouter une escale',
    stopoverCounty: "Comté de l'escale",
    stopoverStation: "Station / Ville de l'escale",
    stopoverArrivalTime: "Heure d'arrivée à l'escale",
    stopoverDuration: "Durée à l'escale (min)",
    removeStopover: "Supprimer l'escale",
    stopoverNote: 'Les escales aident les expéditeurs sur votre itinéraire à trouver votre voyage',
    preferences: 'Préférences',
    language: 'Langue',
    currency: 'Devise',
    notificationSettings: 'Paramètres de notification',
    emailNotifications: 'Notifications par e-mail',
    smsNotifications: 'Notifications SMS',
    pushNotifications: 'Notifications push',
    savePreferences: 'Enregistrer les préférences',
    preferencesSaved: 'Préférences enregistrées!',
    myProfile: 'Mon profil',
    fullName: 'Nom complet',
    email: 'E-mail',
    phone: 'Téléphone',
    changePhoto: 'Changer la photo',
    saveProfile: 'Enregistrer le profil',
    profileSaved: 'Profil enregistré avec succès!',
    availableTrips: 'Voyages disponibles',
    placeBid: 'Faire une offre',
    bidAmount: "Montant de l'offre",
    tripDetails: 'Détails du voyage',
    createNewOrder: 'Créer une nouvelle commande',
    orderDetails: 'Détails de la commande',
    parcelDetails: 'Détails du colis',
    main: 'Principal',
    deliveries: 'Livraisons',
    finance: 'Finance',
    account: 'Compte',
    verified: 'Vérifié',
    pending: 'En attente',
    active: 'Actif',
    completed: 'Terminé',
    cancelled: 'Annulé',
    open: 'Ouvert',
    bidDetails: "Détails de l'offre",
    deliveryDetails: 'Détails de la livraison',
    studentKyc: 'KYC étudiant',
    welcomeBack: 'Bon retour',
    activitySnapshot: 'Voici un aperçu de votre activité',
    activeOrders: 'Commandes actives',
    completedOrders: 'Terminées',
    totalSpent: 'Total dépensé',
    avgRating: 'Note moyenne',
    inProgress: 'En cours',
    deliveredOrders: 'Commandes livrées',
    paymentsMade: 'Paiements effectués',
    fromTravelers: 'Des voyageurs',
    trustScore: 'Score de confiance',
    totalDeliveries: 'livraisons totales',
    totalOrders: 'Total commandes',
    successRate: 'Taux de réussite',
    recentOrders: 'Commandes récentes',
    yourLastOrders: 'Vos dernières',
    noOrdersYet: 'Pas encore de commandes',
    createFirstOrder: 'Créez votre première commande pour commencer',
    createOrder: 'Créer une commande',
    trackDeliveries: 'Suivre les livraisons',
    myReviews: 'Mes avis',
    orderStatusOpen: 'Ouvert',
    orderStatusMatched: 'Associé',
    orderStatusInTransit: 'En transit',
    orderStatusDelivered: 'Livré',
    orderStatusCancelled: 'Annulé',
    orderStatusClosed: 'Fermé',
    trustElite: 'Client élite',
    trustTrusted: 'Client de confiance',
    trustVerified: 'Client vérifié',
    trustActive: 'Client actif',
    trustNew: 'Nouveau client',
    trustMsgElite: 'Vous êtes un expéditeur très fiable sur MuyuRide.',
    trustMsgGood: 'Complétez plus de commandes pour atteindre le statut Élite.',
    trustMsgBuild: 'Construisez votre réputation avec des livraisons réussies.',
    bid: 'offre',
    bids: 'offres',
  },
  ZH: {
    home: '首页',
    dashboard: '仪表板',
    marketplace: '市场',
    createOrder: '创建订单',
    browseTrips: '浏览行程',
    myOrders: '我的订单',
    activeDeliveries: '进行中的配送',
    liveTracking: '实时追踪',
    storage: '存储',
    travelChat: '旅行聊天',
    myFinances: '我的财务',
    payments: '付款',
    notifications: '通知',
    disputes: '纠纷',
    reviews: '评价',
    helpCenter: '帮助中心',
    profile: '个人资料',
    publishTrip: '发布行程',
    myTrips: '我的行程',
    myBids: '我的出价',
    earnings: '收益',
    wallet: '钱包',
    getVerified: '获得认证',
    settings: '设置',
    signOut: '退出登录',
    save: '保存',
    cancel: '取消',
    submit: '提交',
    next: '下一步',
    back: '返回',
    loading: '加载中…',
    saving: '保存中…',
    success: '成功',
    error: '错误',
    search: '搜索',
    filter: '筛选',
    noResults: '未找到结果',
    viewAll: '查看全部',
    close: '关闭',
    confirm: '确认',
    delete: '删除',
    edit: '编辑',
    add: '添加',
    remove: '移除',
    publishATrip: '发布行程',
    routeAndDate: '路线和日期',
    transport: '交通方式',
    capacity: '容量',
    guarantor: '担保人',
    review: '审核',
    originCounty: '出发县',
    destinationCounty: '目的地县',
    travelDate: '出行日期',
    departureTime: '出发时间',
    estimatedArrival: '预计到达',
    arrivalDate: '到达日期',
    arrivalTime: '到达时间',
    departureStation: '出发站',
    arrivalStation: '到达站',
    pickupAddress: '取货地址',
    dropoffAddress: '送货地址',
    stopovers: '中途停靠',
    addStopover: '添加停靠点',
    stopoverCounty: '停靠县',
    stopoverStation: '停靠站 / 城镇',
    stopoverArrivalTime: '到达停靠点时间',
    stopoverDuration: '停靠时长（分钟）',
    removeStopover: '移除停靠点',
    stopoverNote: '中途停靠帮助沿途发件人找到您的行程',
    preferences: '偏好设置',
    language: '语言',
    currency: '货币',
    notificationSettings: '通知设置',
    emailNotifications: '邮件通知',
    smsNotifications: '短信通知',
    pushNotifications: '推送通知',
    savePreferences: '保存偏好',
    preferencesSaved: '偏好已保存！',
    myProfile: '我的资料',
    fullName: '全名',
    email: '邮箱',
    phone: '电话',
    changePhoto: '更换照片',
    saveProfile: '保存资料',
    profileSaved: '资料保存成功！',
    availableTrips: '可用行程',
    placeBid: '出价',
    bidAmount: '出价金额',
    tripDetails: '行程详情',
    createNewOrder: '创建新订单',
    orderDetails: '订单详情',
    parcelDetails: '包裹详情',
    main: '主要',
    deliveries: '配送',
    finance: '财务',
    account: '账户',
    verified: '已认证',
    pending: '待处理',
    active: '活跃',
    completed: '已完成',
    cancelled: '已取消',
    open: '开放',
    bidDetails: '出价详情',
    deliveryDetails: '配送详情',
    studentKyc: '学生KYC',
    welcomeBack: '欢迎回来',
    activitySnapshot: '以下是您的活动概览',
    activeOrders: '活跃订单',
    completedOrders: '已完成',
    totalSpent: '总花费',
    avgRating: '平均评分',
    inProgress: '进行中',
    deliveredOrders: '已配送订单',
    paymentsMade: '已付款',
    fromTravelers: '来自旅行者',
    trustScore: '信任评分',
    totalDeliveries: '总配送次数',
    totalOrders: '总订单数',
    successRate: '成功率',
    recentOrders: '最近订单',
    yourLastOrders: '您的最近',
    noOrdersYet: '暂无订单',
    createFirstOrder: '创建您的第一个订单以开始',
    createOrder: '创建订单',
    trackDeliveries: '追踪配送',
    myReviews: '我的评价',
    orderStatusOpen: '开放',
    orderStatusMatched: '已匹配',
    orderStatusInTransit: '运输中',
    orderStatusDelivered: '已送达',
    orderStatusCancelled: '已取消',
    orderStatusClosed: '已关闭',
    trustElite: '精英客户',
    trustTrusted: '可信客户',
    trustVerified: '认证客户',
    trustActive: '活跃客户',
    trustNew: '新客户',
    trustMsgElite: '您是MuyuRide上高度可信的发件人。',
    trustMsgGood: '完成更多订单以达到精英状态。',
    trustMsgBuild: '通过成功的配送建立您的声誉。',
    bid: '出价',
    bids: '出价',
  },
};

export function getTranslations(language: Language): Translations {
  return translations[language] || translations['EN'];
}
